import {
  PROTOCOL_VERSION,
  parseProtocolMessage,
  type ProtocolMessage,
  type ProtocolPayloads,
} from '@screenshare/protocol';
import { isLoopbackHostname } from '@screenshare/shared';

export class RoomApiError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'RoomApiError';
  }
}

export interface RoomApiClientOptions {
  readonly baseUrl: string;
  readonly fetch?: typeof globalThis.fetch;
  readonly createMessageId?: () => string;
}

export interface JoinIntent {
  readonly roomCode: string;
  readonly inviteToken: string | null;
}

export class RoomApiClient {
  private readonly request: typeof globalThis.fetch;
  private readonly createMessageId: () => string;

  constructor(private readonly options: RoomApiClientOptions) {
    this.request = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.createMessageId = options.createMessageId ?? (() => crypto.randomUUID());
  }

  async createRoom(
    durationSeconds: number | null = null,
  ): Promise<ProtocolPayloads['room.created']> {
    const message: ProtocolMessage<'room.create'> = {
      v: PROTOCOL_VERSION,
      type: 'room.create',
      id: this.createMessageId(),
      epoch: 1,
      payload: { durationSeconds },
    };
    let response: Response;
    try {
      response = await this.request(this.endpoint('/api/rooms'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(message),
      });
    } catch (cause) {
      throw new RoomApiError('The room service is unavailable. Check your connection.', { cause });
    }

    const body = await response.text();
    const parsed = parseProtocolMessage(body);
    if (!response.ok || !parsed.ok || parsed.message.type !== 'room.created') {
      const code =
        parsed.ok && 'code' in parsed.message.payload ? parsed.message.payload.code : null;
      throw new RoomApiError(
        code === 'INTERNAL_ERROR'
          ? 'A room could not be created right now. Please try again.'
          : 'The room service returned an unexpected response.',
      );
    }
    return normalizeRoomCreated(parsed.message.payload, this.options.baseUrl);
  }

  signalingUrl(roomCode: string): string {
    const url = new URL(this.endpoint(`/api/rooms/${encodeURIComponent(roomCode)}/signal`));
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return url.toString();
  }

  private endpoint(pathname: string): string {
    const base = new URL(this.options.baseUrl);
    return new URL(pathname, base).toString();
  }
}

export function readJoinIntent(location: Pick<Location, 'search' | 'hash'>): JoinIntent | null {
  const roomCode = new URLSearchParams(location.search).get('room')?.trim() ?? '';
  if (!/^\d{6}$/u.test(roomCode)) return null;
  const inviteToken = new URLSearchParams(location.hash.replace(/^#/u, '')).get('invite');
  return Object.freeze({
    roomCode,
    inviteToken:
      inviteToken !== null && /^[A-Za-z0-9_-]{32,128}$/u.test(inviteToken) ? inviteToken : null,
  });
}

export function resolveApiBaseUrl(configured: string | undefined, pageUrl: string): string {
  const page = new URL(pageUrl);
  const trimmed = configured?.trim();
  const configuredUrl = new URL(trimmed && trimmed !== '' ? trimmed : pageUrl);
  if (!isLoopbackHostname(page.hostname) && isLoopbackHostname(configuredUrl.hostname)) {
    return page.origin;
  }
  return configuredUrl.origin;
}

function normalizeRoomCreated(
  room: ProtocolPayloads['room.created'],
  apiBaseUrl: string,
): ProtocolPayloads['room.created'] {
  const invite = new URL(room.inviteUrl);
  const api = new URL(apiBaseUrl);
  if (!isLoopbackHostname(invite.hostname) || isLoopbackHostname(api.hostname)) return room;
  invite.protocol = api.protocol;
  invite.hostname = api.hostname;
  invite.port = api.port;
  return Object.freeze({ ...room, inviteUrl: invite.toString() });
}
